import { campusHeader } from './campus';
import type { AuthResult } from './types';

/**
 * The API base URL.
 *
 * Relative by default (""), meaning requests go to the SAME origin as the app —
 * which is what we want whenever something proxies /api onto that origin:
 *   - dev        → the Vite proxy forwards /api to localhost:5080
 *   - Cloudflare → public/_redirects proxies /api/* to Railway
 *
 * Set VITE_API_BASE to point the browser at a DIFFERENT origin instead.
 * ⚠ Doing so makes CORS load-bearing — see vite-env.d.ts.
 *
 * A trailing slash here would produce "//api/..." — so it's stripped, once, here,
 * rather than trusting every caller to get it right.
 */
export const API_BASE: string = (import.meta.env.VITE_API_BASE ?? '').replace(/[/]+$/, '');

/** Turn an app-relative path ("/api/students") into the URL to actually fetch. */
/**
 * The src for a file the SERVER told us about — a logo, a hero image, a crest.
 *
 * ⚠ apiUrl() IS ONLY CORRECT FOR A RELATIVE PATH. A stored URL may already be absolute (an R2
 * public URL, a seeded https:// link) or a data:/blob: URL, and prefixing one of those produces
 * `https://api.eduvoratech.com/https://…` — a 404, an onError, and an image that silently does not
 * appear. A school uploading its crest and seeing nothing is a bad first five minutes, and the bug
 * is invisible in code review because the code reads perfectly.
 *
 * ⚠ THIS LIVES HERE BECAUSE IT WAS COPIED THREE TIMES AND MISSED TWICE. The question paper and the
 * attendance register each grew their own private copy; the branding page and the public-site
 * editor never got one. One exported function is the fix.
 */
export function storedFileSrc(url: string | null | undefined): string | null {
  if (!url) return null;
  return /^(https?:|data:|blob:)/i.test(url) ? url : apiUrl(url);
}

export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

// ---- tenant + token storage ----
// The tenant is resolved from the subdomain in production. For local dev we fall
// back to a stored slug so you can test multiple tenants against localhost.
const TENANT_KEY = 'eduvora.tenant';
const ACCESS_KEY = 'eduvora.access';
const REFRESH_KEY = 'eduvora.refresh';

export function resolveTenantSlug(): string | null {
  const host = window.location.hostname;
  const parts = host.split('.');
  const RESERVED = ['www', 'app', 'api', 'admin'];

  // {slug}.eduvoratech.com  → slug is parts[0], ignoring reserved
  if (parts.length >= 3 && !RESERVED.includes(parts[0])) {
    return parts[0];
  }
  // ?tenant= in the URL is explicit intent — honour it and remember it.
  const q = new URLSearchParams(window.location.search).get('tenant');
  if (q) { localStorage.setItem(TENANT_KEY, q); return q; }

  // ⚠ A RESERVED subdomain (admin./api./app.) is never a tenant. It must NOT fall through to the
  // remembered slug below, or the platform console inherits whatever school was last opened on this
  // browser — sending tenant-scoped headers from an admin surface.
  if (parts.length >= 3 && RESERVED.includes(parts[0]) && parts[0] !== 'www') return null;

  // ⚠ On the BARE ROOT DOMAIN a remembered slug must not hijack the MARKETING entry points.
  // Previously it did, so once any tenant had been opened on this browser the landing page became
  // permanently unreachable — worst on the machine used to demo the product.
  //
  // But the block applies ONLY to the marketing paths. A deep link like eduvoratech.com/admin or
  // /students is someone working in the app, and must still resolve to the remembered school —
  // otherwise the app loads with no tenant and every request 401s.
  const isRootDomain = parts.length <= 2 || parts[0] === 'www';
  const isLocalhost = host === 'localhost' || host === '127.0.0.1';
  const p = window.location.pathname.replace(/\/+$/, '');
  const isMarketingPath = p === '' || p === '/home' || p === '/landing'
    || p === '/terms' || p === '/privacy' || p === '/delete-account';
  if (isRootDomain && !isLocalhost && isMarketingPath) return null;

  return localStorage.getItem(TENANT_KEY);
}

export function setTenantSlug(slug: string) { localStorage.setItem(TENANT_KEY, slug); }

export const tokens = {
  access: () => localStorage.getItem(ACCESS_KEY),
  refresh: () => localStorage.getItem(REFRESH_KEY),
  save(a: AuthResult) {
    localStorage.setItem(ACCESS_KEY, a.accessToken);
    localStorage.setItem(REFRESH_KEY, a.refreshToken);
  },
  clear() { localStorage.removeItem(ACCESS_KEY); localStorage.removeItem(REFRESH_KEY); },
};

// ---- support impersonation marker (set by the platform console on handoff) ----
export interface ImpMarker { sessionId: string; tenantName: string; tenantSlug: string; expiresAt: number; }
const IMP_MARKER = 'eduvora.imp';

/**
 * Consume a support-access handoff arriving in the URL fragment (#imp=…).
 *
 * The platform console lives on admin.<domain> and a school on <slug>.<domain>. localStorage is
 * per-origin, so a token written by the console is invisible to the school's app — the session has
 * to travel in the URL. It rides in the FRAGMENT, which browsers never send to the server, so it
 * stays out of server logs and Referer headers.
 *
 * Called once, as early as possible, before anything reads the token. The fragment is stripped from
 * the address bar immediately so the token isn't left sitting in the URL or in history.
 */
export function consumeImpersonationHandoff(): boolean {
  const hash = window.location.hash;
  if (!hash.startsWith('#imp=')) return false;
  try {
    const raw = decodeURIComponent(hash.slice('#imp='.length));
    const d = JSON.parse(raw) as { t: string; sid: string; name: string; slug: string; exp: number };
    if (!d?.t) return false;

    localStorage.setItem(ACCESS_KEY, d.t);
    // impersonation is non-refreshable by design — make sure no stale refresh token survives
    localStorage.removeItem(REFRESH_KEY);
    localStorage.setItem(TENANT_KEY, d.slug);
    localStorage.setItem(IMP_MARKER, JSON.stringify({
      sessionId: d.sid, tenantName: d.name, tenantSlug: d.slug, expiresAt: d.exp,
    }));
    return true;
  } catch {
    return false;
  } finally {
    // Always clear it, even on failure — a token must not linger in the address bar.
    history.replaceState(null, '', window.location.pathname + window.location.search);
  }
}

export function readImpMarker(): ImpMarker | null {
  try {
    const raw = localStorage.getItem(IMP_MARKER);
    return raw ? (JSON.parse(raw) as ImpMarker) : null;
  } catch { return null; }
}
export function clearImpersonation() {
  localStorage.removeItem(IMP_MARKER);
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

async function raw(path: string, init: RequestInit, retry = true, multipart = false): Promise<Response> {
  const slug = resolveTenantSlug();
  const headers = new Headers(init.headers);

  // ⚠ For a multipart upload the browser must set Content-Type ITSELF, because the header needs a
  // boundary token only it knows. Setting application/json here would produce a body the server
  // cannot parse — and the failure is confusing, because the file is visibly in the request.
  if (!multipart) headers.set('Content-Type', 'application/json');
  if (slug) headers.set('X-Tenant', slug);
  /**
   * ⚠ WHICH CAMPUS. Sent on every request, beside the tenant, and absent when nothing has been
   * chosen — the server distinguishes a deliberate trust-wide read from one that never chose.
   * The server ignores it entirely for a campus-level role, whose institution comes from their
   * token; this header is a hint, never a permission.
   */
  for (const [k, v] of Object.entries(campusHeader())) headers.set(k, v);
  const access = tokens.access();
  if (access) headers.set('Authorization', `Bearer ${access}`);

  const res = await fetch(apiUrl(path), { ...init, headers });

  // one silent refresh attempt on 401
  if (res.status === 401 && retry && tokens.refresh()) {
    const refreshed = await tryRefresh();
    if (refreshed) return raw(path, init, false);
  }
  return res;
}

async function tryRefresh(): Promise<boolean> {
  const slug = resolveTenantSlug();
  const headers = new Headers({ 'Content-Type': 'application/json' });
  if (slug) headers.set('X-Tenant', slug);
  const res = await fetch(apiUrl('/api/auth/refresh'), {
    method: 'POST',
    headers,
    body: JSON.stringify({ refreshToken: tokens.refresh() }),
  });
  if (!res.ok) { tokens.clear(); return false; }
  tokens.save(await res.json());
  return true;
}

async function parse<T>(res: Response): Promise<T> {
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(res.status, body?.error ?? `Request failed (${res.status})`);
  return body as T;
}

export const api = {
  get: <T>(path: string) => raw(path, { method: 'GET' }).then(parse<T>),
  post: <T>(path: string, body?: unknown) =>
    raw(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }).then(parse<T>),
  put: <T>(path: string, body?: unknown) =>
    raw(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }).then(parse<T>),
  del: <T>(path: string) => raw(path, { method: 'DELETE' }).then(parse<T>),

  /**
   * Fetch a response as TEXT rather than JSON.
   *
   * For endpoints that return a whole HTML document — an invoice preview, a printed report card.
   * Needed because an <iframe src="..."> cannot carry the Authorization header: the browser issues
   * that request itself, without the app's token, and the endpoint returns 401. Fetching here and
   * injecting via srcDoc keeps the auth and keeps the document's CSS scoped to the frame.
   */
  /**
   * Fetch a response as binary.
   *
   * For images served by an authenticated endpoint. An <img src="/api/..."> cannot carry the
   * Authorization header — the browser issues that request on its own — so the bytes are fetched
   * here and turned into a blob URL the image tag can use.
   */
  /**
   * POST multipart form data — a file upload.
   *
   * ⚠ Content-Type is DELETED rather than set. The browser must generate it itself, because a
   * multipart body needs a boundary token that only the browser knows. Setting it by hand produces a
   * header with no boundary, and the server rejects the body as malformed — a confusing failure,
   * because the file is right there in the request.
   */
  postForm: async <T>(path: string, body: FormData): Promise<T> => {
    const response = await raw(path, { method: 'POST', body }, true, /* multipart */ true);
    return await parse<T>(response);
  },

  blob: async (path: string): Promise<Blob> => {
    const response = await raw(path, { method: 'GET' });
    return await response.blob();
  },

  getText: async (path: string): Promise<string> => {
    const response = await raw(path, { method: 'GET' });
    return await response.text();
  },
};

/**
 * Unwrap a list response that may be a bare array OR a paged `{ items, total }`.
 *
 * ⚠ Several list endpoints (`/api/students`, `/api/library/books`) were changed from returning
 * `T[]` to returning `PagedResult<T>`. Every caller that kept doing `.map` / `.slice` / `[0]`
 * white-screened the page. This has now happened three times, so unwrapping lives in ONE place
 * rather than being re-remembered at each call site.
 *
 * Use this for every list GET whose shape you are not personally certain of.
 */
/**
 * Open a print-ready document (invoice or receipt) in a new tab.
 *
 * ⚠ These endpoints return HTML, NOT a PDF or JSON. An earlier version of the parent receipt
 * button did `res.blob()` and saved the result as ".pdf", which produced a mislabelled file no
 * reader could open. Every browser has "Save as PDF" in its print dialog, so we ship no PDF engine
 * and the user still gets a PDF.
 *
 * ⚠ Fetched with the auth header rather than opened directly, because these routes require a
 * bearer token — a plain window.open() would land on a 401 page.
 */
export async function openDocument(path: string): Promise<void> {
  const slug = resolveTenantSlug();
  const res = await fetch(apiUrl(path), {
    headers: {
      ...(slug ? { 'X-Tenant': slug } : {}),
      ...(tokens.access() ? { Authorization: `Bearer ${tokens.access()}` } : {}),
    },
  });
  if (!res.ok) throw new ApiError(res.status, 'Could not open the document.');

  const html = await res.text();
  // A blob URL rather than document.write into a fresh window: popup blockers stop a written
  // document far more often than an opened URL.
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  const w = window.open(url, '_blank');
  if (!w) throw new ApiError(0, 'Please allow pop-ups to view the document.');
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function asList<T>(r: T[] | { items: T[] } | null | undefined): T[] {
  if (Array.isArray(r)) return r;
  if (r && Array.isArray((r as { items?: T[] }).items)) return (r as { items: T[] }).items;
  return [];
}

export { ApiError };
